import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import DropDown from '../../../cm/common/DropDown';
import { Button, Col, Input, InputNumber, Row } from 'antd';
import ControlWithLabel from '../../../cm/common/ControlWithLabel';
import { StyledInputGroup, StyledSlider } from './styled';
import { isNumber } from 'lodash';

const marks = {
  0: {
    style: {
      color: '#71C969',
      marginLeft: '24px'
    },
    label: 'Lower Limit'
  },
  100: {
    style: {
      color: '#E84D42',
      width: '100%',
      left: '93%'
    },
    label: 'Upper Limit'
  }
};

const ListFilters = ({
  className,
  filters,
  onChange,
  customerType,
  setIsDropdownVisible
}) => {
  const [alertStatus, setAlertStatus] = useState(null);
  const [segmentName, setSegmentName] = useState(null);
  const [alertScore, setAlertScore] = useState([]);

  useEffect(() => {
    setAlertStatus(filters?.alertStatus);
    setSegmentName(filters?.segmentName);
    setAlertScore(filters?.alertScore);
  }, [filters]);

  const segmentNameList = useMemo(() => {
    return customerType.map((cus) => ({
      label: cus.customer.displayName,
      value: cus.customer.displayName
    }));
  }, [customerType]);

  const onChangeAlertScoreSlider = (value) => {
    const min = value[0] > 99.99 ? 99.99 : value[0];
    const max = value[1] < 0.01 ? 0.01 : value[1];

    setAlertScore([min || 0, max]);
  };

  const onChangeAlertScore = (value, isLowValueChanging) => {
    let score = value;

    if (isLowValueChanging && value[0] >= value[1]) {
      score[1] = value[0] + 0.01;
    }

    setAlertScore(score);
  };

  const validateNumberInput = (e) => {
    const pattern = new RegExp(/^\d+$/);
    return pattern.test(e.key) || e.key === '' ? e : e.preventDefault();
  };

  const onClearClick = () => {
    // Close the dropdown
    setIsDropdownVisible(false);

    if (
      filters?.alertStatus ||
      filters?.segmentName ||
      filters?.alertScore?.length > 0
    ) {
      onChange({
        ...filters,
        alertStatus: undefined,
        segmentName: undefined,
        alertScore: []
      });
    } else {
      setAlertStatus(filters?.alertStatus);
      setSegmentName(filters?.segmentName);
      setAlertScore(filters?.alertScore);
    }
  };

  const onClickApply = () => {
    // Close the dropdown
    setIsDropdownVisible(false);

    onChange({
      ...filters,
      alertStatus,
      segmentName,
      alertScore
    });
  };

  const isEnabledApplyButton = useMemo(() => {
    return (
      filters?.alertStatus !== alertStatus ||
      filters?.segmentName !== segmentName ||
      filters?.alertScore !== alertScore
    );
  }, [filters, alertScore, alertStatus, segmentName]);

  const isEnabledClearButton = useMemo(() => {
    return (
      alertStatus ||
      segmentName ||
      (isNumber(alertScore?.[0]) && isNumber(alertScore?.[1]))
    );
  }, [alertScore, alertStatus, segmentName]);

  return (
    <Container className={className}>
      <StyledDropDown
        label="Alert Status"
        placeholder={`Please select`}
        options={[
          {
            label: 'Sent to CM',
            value: 'true'
          },
          {
            label: 'Not sent to CM',
            value: 'false'
          }
        ]}
        allowClear
        value={alertStatus}
        onChange={(status) => setAlertStatus(status)}
      />
      {/* <StyledDropDown
        label="Segment"
        placeholder={`Please select`}
        options={[...segmentNameList]}
        allowClear
        value={segmentName}
        onChange={(segName) => setSegmentName(segName)}
      /> */}
      <ControlWithLabel label={`Alert Score`}>
        <Row>
          <Col span={15}>
            <StyledSlider
              range
              step={0.01}
              value={alertScore}
              onChange={onChangeAlertScoreSlider}
              marks={marks}
              trackStyle={[{ backgroundColor: '#91bced', height: '5px' }]}
              handleStyle={[
                { backgroundColor: '#71C969', borderColor: 'white' },
                { backgroundColor: '#E84D42', borderColor: 'white' }
              ]}
            />
          </Col>
          <Col span={9}>
            <StyledInputGroup compact>
              <InputNumber
                className="site-input-left"
                min={0}
                max={99.99}
                precision={2}
                value={alertScore[0]}
                onChange={(value) =>
                  onChangeAlertScore([value, alertScore[1]], true)
                }
                onKeyPress={(e) => validateNumberInput(e)}
                type="number"
              />
              <Input className="site-input-split" placeholder="-" disabled />
              <InputNumber
                className="site-input-right"
                min={(alertScore[0] || 0) + 0.01}
                max={100}
                precision={2}
                value={alertScore[1]}
                onChange={(value) =>
                  onChangeAlertScore([alertScore[0], value], false)
                }
                onKeyPress={(e) => validateNumberInput(e)}
                type="number"
              />
            </StyledInputGroup>
          </Col>
        </Row>
      </ControlWithLabel>
      <div
        className="on-demand-screening-button-css"
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'end',
          gap: '10px'
        }}>
        <Button onClick={onClearClick} disabled={!isEnabledClearButton}>
          Clear
        </Button>
        <Button
          onClick={onClickApply}
          type="primary"
          disabled={!isEnabledApplyButton}>
          Apply
        </Button>
      </div>
    </Container>
  );
};

ListFilters.propTypes = {
  className: PropTypes.string,
  filters: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  setIsDropdownVisible: PropTypes.func.isRequired,
  customerType: PropTypes.array
};

const Container = styled.div`
  display: flex;
  & > * {
    margin-right: 22px;
  }
`;

const StyledDropDown = styled(DropDown)`
  width: 150px;
`;

export default ListFilters;
